import { z } from "zod";
import { SUPPORTED_LANGUAGES } from "./domain/language.ts";

// The components of a composed answer (`service/assistant/answer-layout.ts`), named as in `genui-catalog.ts`.
// The root's draft and the stored answer share this tree; they differ in the leaves only: a cited text and a source.
// Titles, names, labels, terms, columns, issuers and missing data are labels: they cite nothing.
export type AssistantLayoutComponent<Text, Source> =
  | { type: "Summary"; points: Text[] }
  | { type: "Paragraph"; text: Text }
  | { type: "KeyPoint"; tone: "finding" | "caution" | "gap"; text: Text }
  | { type: "StudyCard"; sourceId: Source; takeaway: Text }
  | {
      type: "ReferenceCard";
      sourceId: Source;
      population: Text | null;
      endpoint: Text | null;
      result: Text;
    }
  | {
      type: "EvidenceTable";
      columns: string[];
      rows: { label: string; sourceId: Source | null; cells: Text[] }[];
    }
  | { type: "RecommendationBox"; issuer: string; sourceId: Source; text: Text }
  | {
      type: "Differential";
      likely: { name: string; detail: Text }[];
      dontMiss: { name: string; redFlags: Text }[];
      other: { name: string; detail: Text }[];
    }
  | {
      type: "OrderedSteps";
      steps: { title: string; text: Text; line: "first" | "second" | "none" }[];
    }
  | { type: "MechanismList"; items: { term: string; text: Text }[] }
  | {
      type: "DrugCard";
      name: string;
      indication: Text | null;
      dose: Text;
      renal: Text | null;
      interactions: Text | null;
    }
  | {
      type: "Debated";
      question: Text;
      positions: { label: string; text: Text }[];
    }
  | {
      type: "ResearchQuestion";
      title: string;
      question: Text;
      population: Text;
      comparison: Text;
      outcome: Text;
      method: Text;
    }
  | { type: "MissingInformation"; questions: string[] };
// A Section groups the components of one part of the answer; it holds no Section.
export type AssistantLayout<Text, Source> = (
  | AssistantLayoutComponent<Text, Source>
  | {
      type: "Section";
      title: string;
      children: AssistantLayoutComponent<Text, Source>[];
    }
)[];
// The bounds of the genUI catalog; the text lengths stay targets of the prompt.
export function createAssistantAnswerLayoutSchema<Text, Source>(leaf: {
  text: z.ZodType<Text>;
  source: z.ZodType<Source>;
  label: z.ZodType<string>;
}) {
  const { text, source, label } = leaf;
  const component = z.discriminatedUnion("type", [
    z.object({
      type: z.literal("Summary"),
      points: z.array(text).min(1).max(4),
    }),
    z.object({ type: z.literal("Paragraph"), text }),
    z.object({
      type: z.literal("KeyPoint"),
      tone: z.enum(["finding", "caution", "gap"]),
      text,
    }),
    z.object({
      type: z.literal("StudyCard"),
      sourceId: source,
      takeaway: text,
    }),
    z.object({
      type: z.literal("ReferenceCard"),
      sourceId: source,
      population: text.nullable(),
      endpoint: text.nullable(),
      result: text,
    }),
    z
      .object({
        type: z.literal("EvidenceTable"),
        columns: z.array(label).min(1).max(4),
        rows: z
          .array(
            z.object({
              label,
              sourceId: source.nullable(),
              cells: z.array(text),
            }),
          )
          .min(2)
          .max(8),
      })
      .refine(
        (table) =>
          table.rows.every((row) => row.cells.length === table.columns.length),
        "Each row has one cell per column.",
      ),
    z.object({
      type: z.literal("RecommendationBox"),
      issuer: label,
      sourceId: source,
      text,
    }),
    z.object({
      type: z.literal("Differential"),
      likely: z
        .array(z.object({ name: label, detail: text }))
        .min(1)
        .max(4),
      dontMiss: z.array(z.object({ name: label, redFlags: text })).max(4),
      other: z.array(z.object({ name: label, detail: text })).max(6),
    }),
    z.object({
      type: z.literal("OrderedSteps"),
      steps: z
        .array(
          z.object({
            title: label,
            text,
            line: z.enum(["first", "second", "none"]),
          }),
        )
        .min(2)
        .max(8),
    }),
    z.object({
      type: z.literal("MechanismList"),
      items: z
        .array(z.object({ term: label, text }))
        .min(2)
        .max(8),
    }),
    z.object({
      type: z.literal("DrugCard"),
      name: label,
      indication: text.nullable(),
      dose: text,
      renal: text.nullable(),
      interactions: text.nullable(),
    }),
    z.object({
      type: z.literal("Debated"),
      question: text,
      positions: z.array(z.object({ label, text })).min(2).max(4),
    }),
    z.object({
      type: z.literal("ResearchQuestion"),
      title: label,
      question: text,
      population: text,
      comparison: text,
      outcome: text,
      method: text,
    }),
    z.object({
      type: z.literal("MissingInformation"),
      questions: z.array(label).min(1).max(20),
    }),
  ]) satisfies z.ZodType<AssistantLayoutComponent<Text, Source>>;
  const layout = z
    .array(
      z.discriminatedUnion("type", [
        z.object({
          type: z.literal("Section"),
          title: label,
          children: z.array(component).min(1).max(20),
        }),
        ...component.options,
      ]),
    )
    .min(1)
    .max(40)
    .refine(
      (items) =>
        items
          .flatMap((item) => (item.type === "Section" ? item.children : [item]))
          .filter((item) => item.type === "MissingInformation").length <= 1,
      "A layout holds at most one MissingInformation.",
    ) satisfies z.ZodType<AssistantLayout<Text, Source>>;
  return { component, layout };
}
// Each text holds its Markdown with `[n](href)` links and names its answer block; a source is entry n of `sources`.
export const assistantAnswerLayoutSchema = createAssistantAnswerLayoutSchema({
  text: z.object({ block: z.number().int().nonnegative(), text: z.string() }),
  source: z.number().int().positive(),
  label: z.string().min(1),
}).layout;
// A published layout as the webapp renders it: the components, and the language of the answer that their labels follow.
export const assistantAnswerLayoutViewSchema = z.object({
  language: z.enum(SUPPORTED_LANGUAGES),
  components: assistantAnswerLayoutSchema,
});
